import { useState, useEffect } from 'react';
import { Clock, CheckCircle2, AlertTriangle, MessageSquare, UserPlus, FileText, Loader2, Lock, Unlock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { projectId } from '../utils/supabase/info';
import { formatCategoryLabel } from '../utils/normalizeAcronyms';

interface ClientActivityTimelineProps {
  companyId: string;
  accessToken: string;
  limit?: number;
  contextType?: string; // Optional filter
  contextId?: string;
}

interface Activity {
  id: string;
  type: 'activity' | 'message';
  action?: string; // for activity
  content?: string; // for message
  userId: string;
  userName: string;
  timestamp: string;
  details?: string;
}

export function ClientActivityTimeline({ companyId, accessToken, limit = 20, contextType, contextId }: ClientActivityTimelineProps) {
  const [items, setItems] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [companyId, contextType, contextId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // 1. Fetch System Activity
      const activityRes = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/recent-activity?limit=${limit}`,
        { headers: { 'Authorization': `Bearer ${accessToken}` } }
      );
      
      let activities: Activity[] = [];
      if (activityRes.ok) {
        const data = await activityRes.json();
        // Filter by company
        const filtered = data.activities.filter((a: any) => a.companyId === companyId);
        activities = filtered.map((a: any) => ({
          id: a.id,
          type: 'activity',
          action: a.action,
          userId: a.userId,
          userName: a.userName,
          timestamp: a.timestamp,
          details: a.details
        }));
      }

      // 2. Fetch Messages (Contextual Comments)
      let url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/messages?companyId=${companyId}`;
      if (contextType && contextId) {
        url += `&contextType=${contextType}&contextId=${contextId}`;
      }
      
      const messagesRes = await fetch(url, { headers: { 'Authorization': `Bearer ${accessToken}` } });
      
      let messages: Activity[] = [];
      if (messagesRes.ok) {
        const data = await messagesRes.json();
        messages = data.messages.map((m: any) => ({
          id: m.id,
          type: 'message',
          content: m.content,
          userId: m.senderId,
          userName: m.senderName,
          timestamp: m.createdAt,
          details: m.type === 'system_event' ? 'System Event' : 'Comment'
        }));
      }

      // 3. Merge and Sort
      const allItems = [...activities, ...messages].sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      setItems(allItems.slice(0, limit));

    } catch (error) {
      console.error('Error loading timeline:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (item: Activity) => {
    if (item.type === 'message') return <MessageSquare className="size-4 text-blue-500" />;
    
    switch (item.action) {
      case 'approved': return <CheckCircle2 className="size-4 text-green-600" />;
      case 'completed': return <CheckCircle2 className="size-4 text-green-600" />;
      case 'locked': return <Lock className="size-4 text-orange-600" />;
      case 'unlocked': return <Unlock className="size-4 text-green-600" />;
      case 'assigned': return <UserPlus className="size-4 text-purple-600" />;
      case 'blocked': return <AlertTriangle className="size-4 text-red-600" />;
      default: return <Clock className="size-4 text-gray-600" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="size-5" />
          Activity Feed
        </CardTitle>
        <CardDescription>Unified timeline of actions and comments</CardDescription>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">No recent activity</div>
        ) : (
          <div className="space-y-4">
            {items.map((item, index) => (
              <div key={item.id} className="relative flex gap-3">
                {index < items.length - 1 && (
                  <div className="absolute left-[11px] top-8 bottom-[-16px] w-0.5 bg-gray-100" />
                )}
                
                <div className={`mt-1 p-1 rounded-full bg-gray-50 border border-gray-200 h-fit`}>
                  {getIcon(item)}
                </div>
                
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <span className="font-medium text-sm">{item.userName}</span>
                    <span className="text-xs text-muted-foreground">{formatTimestamp(item.timestamp)}</span>
                  </div>
                  
                  {item.type === 'message' ? (
                    <div className="mt-1 text-sm bg-blue-50/50 p-2 rounded border border-blue-100">
                      {item.content}
                    </div>
                  ) : (
                    <div className="mt-1 text-sm text-gray-600">
                      {formatCategoryLabel(item.action || '')} {item.details && `- ${item.details}`}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}